import { useGetOrganizationApps, usePublicApps } from "@/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import Loading from "@/components/ui/loading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearch } from "@tanstack/react-router";
import { LayoutGrid, Plus, Table } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";
import { AppGalleryView } from "./Gallery/AppGalleryView";
import NoOrganizationApps from "./NoOrganizationApps";
import QuickCreateAppModal from "./QuickCreateApp/QuickCreateAppModal";
import { AppTableView } from "./Table/AppTableView";

type Props = {
  organizationName?: string;
  organizationSlug?: string;
};

export const OrganizationApps = ({
  organizationName = "",
  organizationSlug = ""
}: Props) => {
  const [tabValue, setTabValue] = useLocalStorage(
    "appCardPreferredView",
    "gallery"
  );
  const { filterShowPublicApps } = useSearch({
    from: "/_auth/_fixed-sidebar/_organization-access/organization/$organizationSlug/gallery"
  });
  const {
    data: organizationApps,
    loading: loadingOrganizationApps,
    error: organizationAppsError,
    called: organizationAppsCalled
  } = useGetOrganizationApps({ organizationSlug });
  const {
    data: publicApps,
    loading: loadingPublicApps,
    error: publicAppsError
  } = usePublicApps();

  const allApps = [
    ...organizationApps,
    ...(filterShowPublicApps ? publicApps : [])
  ];
  const sortedAllApps = allApps.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  if (loadingOrganizationApps || !organizationAppsCalled)
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Loading size="sm" />
      </div>
    );

  return (
    <Tabs
      value={tabValue}
      onValueChange={(val) => setTabValue(val)}
      className="w-full"
    >
      <div className="flex flex-col items-center mb-2 w-full">
        <PageHeader
          header={`Your Organization's Apps`}
          subHeader={organizationName}
        >
          <div className="flex items-center gap-6">
            <QuickCreateAppModal>
              <Button
                className="flex items-center gap-1 mr-0 ml-auto w-fit"
                variant="secondary"
              >
                Deploy an app
                <Plus size={16} />
              </Button>
            </QuickCreateAppModal>

            <TabsList>
              <TabsTrigger value="gallery">
                <LayoutGrid size={16} />
              </TabsTrigger>
              <TabsTrigger value="table">
                <Table size={16} />
              </TabsTrigger>
            </TabsList>
          </div>
        </PageHeader>
        <div className="w-full">
          {organizationApps.length ? (
            <>
              <TabsContent value="gallery">
                <AppGalleryView
                  apps={sortedAllApps}
                  loading={false}
                  errors={{
                    organizationAppsError,
                    publicAppsError: filterShowPublicApps
                      ? publicAppsError
                      : undefined
                  }}
                />
              </TabsContent>
              <TabsContent value="table">
                <AppTableView
                  apps={sortedAllApps}
                  loading={false}
                  errors={{
                    organizationAppsError,
                    publicAppsError: filterShowPublicApps
                      ? publicAppsError
                      : undefined
                  }}
                />
              </TabsContent>
            </>
          ) : (
            <NoOrganizationApps
              publicApps={publicApps}
              loadingPublicApps={loadingPublicApps}
              publicAppsError={publicAppsError}
            />
          )}
        </div>
      </div>
    </Tabs>
  );
};
